import {useLocale, useMessages} from "i18n";
import formatBytes from "core/format/byteFormat";
import {formatMoney} from "pricing/PricingStorage";
import {SlideOutCard} from "home/components/SlideOutCard";
import type {SlideOutCardControls} from "home/components/useSlideOutCards";
import {limitWallCardLine, type LimitWallCardModel} from "./limitWallOffer";

export interface LimitWallOfferCardProps {
	/** From {@link limitWallCard}; null renders nothing. */
	model: LimitWallCardModel | null;
	/** Open or tucked, and the lift: owned by the panel through `useSlideOutCards`, never held here. */
	controls: SlideOutCardControls;
	/**
	 * The reason line of a same-plan card this offer replaced (`slideOutCardsBesideOffer`), which leads the
	 * card's one line in place of the per-transfer limit; null when no card was replaced.
	 */
	lead: string | null;
	onAction: () => void;
}

/** The offer's tag, in the open card's chip and on the tucked chip alike: a coupon, not a warning and not an arrow. */
function OfferGlyph() {
	return (
		<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/>
			<circle cx="7.5" cy="7.5" r="1.5"/>
		</svg>
	);
}

/**
 * The limit-wall email's offer card (kitecorp/transferify-ui#3197): the first member of the send screen's
 * slide-out family, shown to a sender who followed the email back to the composer.
 *
 * Everything about the shell belongs to {@link SlideOutCard}, which the warning and upsell cards share;
 * it wears the upsell's brand-blue accent because it is an ask. What lives here is what makes this card
 * the OFFER: the size the wall saw in its title, the api's two prices on its line, and the one button
 * that opens the coupon checkout over the composer. Title, one line, one button; no meter, no countdown,
 * no note about files and no percentage (ui#2545). The button stands alone in its row, with no glyph chip
 * beside it.
 *
 * Every figure is formatted from the api's own answer: the size in the reader's locale with one decimal
 * ("18.4 GB") and the limit with none ("50 GB"), the money in the currency the resolve quoted. Nothing
 * here reads the baked catalogue.
 */
export function LimitWallOfferCard({model, controls, lead, onAction}: LimitWallOfferCardProps) {
	const {offerLadder, pricing, common} = useMessages();
	const {locale} = useLocale();
	if (model === null) {
		return null;
	}
	const copy = offerLadder.limitWall.card;
	const plan = pricing.planNames[model.plan];
	const title = model.title.kind === "size" ? copy.sizeTitle(formatBytes(model.title.bytes, locale, 1, 0), plan)
		: model.title.kind === "covers" ? copy.coversTitle(plan)
		: copy.plainTitle(plan);
	const renewal = formatMoney(model.currency, model.renewal, locale);
	const price = model.firstMonth === null
		? copy.regularLine(renewal)
		: copy.priceLine(formatMoney(model.currency, model.firstMonth, locale), renewal);
	const limit = model.maxHostedUploadBytes === null ? null : copy.limitLine(formatBytes(model.maxHostedUploadBytes, locale, 0));
	return (
		<SlideOutCard
			accent="upsell"
			open={controls.open}
			lifting={controls.lifting}
			offsetTop={controls.offsetTop}
			stacked={controls.stacked}
			onHeight={controls.onHeight}
			onOpen={controls.openCard}
			onTuck={controls.closeCard}
			testId="limit-wall-offer-card"
			data-limit-wall-title={model.title.kind}
			title={title}
			line={limitWallCardLine(lead, price, limit)}
			showLabel={copy.show}
			tuckLabel={common.tuckTag}
			glyph={<OfferGlyph/>}
			actionLabel={model.discounted ? copy.useDiscount : offerLadder.gift.subscribe}
			actionAlone
			onAction={onAction}
		/>
	);
}
